import { historicalProductName, historicalSku, historicalVariantOptions, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { hasRole, canOperateInventory, canManageSalesOrders } from "@/lib/permissions";
import { FULFILLABLE_STATUSES, ALLOCATABLE_STATUSES, isCancellable, lineSku, lineVariantLabel, marketplaceFulfilmentSplit, operationalQtyCaption, orderPaymentChannelLayers, remainingFulfilmentQty, residualQtyLabel, shouldPromoteAllocateCta } from "@/lib/sales-orders";
import { canEditSalesOrderLines, shouldShowEditOrderCta, valuesFromOrder } from "@/lib/sales-order-edit";
import { OrderAttentionCard } from "@/components/base/OrderAttentionCard";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { ActionMenu, type ActionMenuItem } from "@/components/base/ActionMenu";
import { money, fmtDate, fmtDateTime, variantLabel } from "@/lib/format";
import { buildProvenance, provenanceLabel } from "@/lib/order-provenance";
import * as salesApi from "@/lib/api/sales-orders";
import * as warehouseApi from "@/lib/api/warehouse";
import { syncBigCommerceFulfilment, syncEbayFulfilment } from "@/lib/api/marketplace-fulfilment";
import { firstRelation } from "@/lib/relations";
import { availableForExactVariantLocation, balanceKey, shouldLoadOrderAvailability, buildFulfilmentRequest, seedLineFulfilmentLocations, fulfilmentConfirmDisabled } from "@/lib/fulfilment-location";
import { restoreManualTrackingCarrier, storedManualTrackingMatches } from "@/lib/manual-tracking";
import { MANUAL_SHIPPING_CARRIERS, mapCarrierToBcShippingProvider } from "@/lib/providers/bigcommerce-fulfilment";
import { trackingCta, retryChannelSyncLabel, shipmentTrackingSavedCopy, shipmentChannelPendingCopy } from "@/lib/phase1-sync-contract";
import { australiaPostTrackingUrl, isAustraliaPostShipment } from "@/lib/shipment-timeline";
import { EditSalesOrderModal, type SalesOrderEditValues } from "./components/EditSalesOrderModal";
import { FulfilOrderModal } from "../components/FulfilOrderModal";
import { PrintDocumentsMenu } from "../components/PrintDocumentsMenu";
import { addressToJson } from "@/components/feature/AddressFields";
import { canPrintOrderDocuments, shippingLabelPrintState, type OrderDocumentKind } from "@/lib/order-documents";
import { printSalesOrderDocuments } from "@/lib/api/order-documents";
import { fulfilmentShipmentPath } from "@/lib/fulfilment-workspace";
import * as returnsApi from "@/lib/api/returns";
import { syncMarketplaceRefund, syncMarketplaceCancel } from "@/lib/api/marketplace-reverse";
import {
  ORDER_CANCEL_COPY,
  EBAY_CANCEL_REASON_OPTIONS,
  ebayCancelReasonIsSellerFault,
  isEbayCancelReasonCode,
  REFUND_AFTER_RETURN_COPY,
  canCreateSalesReturn,
  remainingCancelLinePreviews,
  remainingOrderCancelMustStayLocal,
  hasCompletedSalesReturn,
  MARKETPLACE_STATUS_INDEPENDENCE_COPY,
  CHANNEL_CANCEL_REVERSAL_COPY,
  channelCancellationConfirmedLabel,
  canRefundSalesOrder,
  composeCompensation,
  composeRefundExposure,
  refundedUnfulfilledCancellableQty,
  remainingRefundableQty,
  requiresUnfulfilledDisposition,
  type CompensationMethod,
  type RefundLineState,
  type UnfulfilledRefundDisposition,
} from "@/lib/reverse-lifecycle";
import { RefundComposer } from "@/components/feature/RefundComposer";
import { inboundAfterSalesUiRows } from "@/lib/inbound-after-sales";
import { buildSalesOrderAttentionItems } from "@/lib/order-attention";

interface SoRow {
  id: string;
  order_number: string;
  customer_id: string | null;
  status: string;
  order_date: string | null;
  subtotal: number | null;
  currency: string | null;
  total: number;
  notes: string | null;
  source: string;
  channel_id: string | null;
  location_id: string | null;
  payment_status: string | null;
  provider_cancel_status: string | null;
  provider_cancel_error: string | null;
  provider_cancel_reason: string | null;
  provider_cancelled_at: string | null;
  priority: string | null;
  customer_reference: string | null;
  discount: number | null;
  tax: number | null;
  shipping: number | null;
  billing_address: Record<string, string> | null;
  shipping_address: Record<string, string> | null;
  customers?: { name: string } | null;
  locations?: { name: string } | null;
  connectors?: { name: string; provider: string | null } | null;
}

interface SoItemRow extends CatalogueHistoryFields {
  id: string;
  product_id: string | null;
  variant_id: string | null;
  qty_ordered: number;
  qty_allocated: number;
  qty_fulfilled: number;
  qty_backordered: number | null;
  qty_cancelled: number | null;
  unit_price: number;
  total: number;
  is_bundle: boolean | null;
  bundle_parent_item_id: string | null;
  product_name: string | null;
  sku: string | null;
  variant_attributes: Record<string, string> | null;
  line_discount: number | null;
  line_tax: number | null;
  products?: { name: string; sku: string } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
}

interface FulfilmentRow {
  id: string;
  fulfilment_number: string;
  status: string;
  shipped_at: string | null;
  notes: string | null;
  locations?: { name: string } | null;
}

interface FulfilmentItemRow extends CatalogueHistoryFields {
  id: string;
  fulfilment_id: string;
  product_id: string;
  qty_fulfilled: number;
  products?: { name: string; sku: string } | null;
}

interface ShipmentRow {
  id: string;
  fulfilment_id: string;
  carrier_service_id: string | null;
  status: string;
  tracking_number: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  carrier_reference: string | null;
  idempotency_key: string | null;
  tracking_status: string | null;
  service_name: string | null;
  shipping_provider: string | null;
  provider_sync_status: string | null;
  provider_sync_error: string | null;
  provider_shipment_id: string | null;
  label_url: string | null;
  carrier_services?: { name: string } | null;
}

interface ReturnRow {
  id: string;
  return_number: string;
  status: string;
  reason: string | null;
  created_at: string;
}

interface RefundRow {
  id: string;
  amount: number;
  currency: string | null;
  status: string;
  reason: string | null;
  provider_sync_status: string | null;
  refund_mode: string | null;
  shipping_amount: number | null;
  created_at: string;
}

interface RefundItemRow {
  sales_refund_id: string;
  sales_order_item_id: string;
  sku: string | null;
  qty: number;
  amount: number;
  qty_unfulfilled_cancelled?: number | null;
}

interface CarrierRow {
  id: string;
  name: string;
  carrier_code: string;
}

function acceptedRefunds(refunds: RefundRow[]) {
  return refunds.filter((r) => r.status !== "rejected" && r.status !== "failed");
}

function refundLineStates(items: SoItemRow[], refunds: RefundRow[], refundItems: RefundItemRow[]): RefundLineState[] {
  const acceptedIds = new Set(acceptedRefunds(refunds).map((r) => r.id));
  return items.filter((it) => !it.is_bundle).map((it) => {
    const rows = refundItems.filter((ri) => ri.sales_order_item_id === it.id && acceptedIds.has(ri.sales_refund_id));
    return {
      salesOrderItemId: it.id,
      productName: historicalProductName(it),
      sku: historicalSku(it),
      variantLabel: variantLabel(historicalVariantOptions(it) ?? it.variant_attributes) || null,
      qtyOrdered: it.qty_ordered,
      qtyAlreadyRefunded: rows.reduce((s, r) => s + Number(r.qty ?? 0), 0),
      lineCharged: it.total,
      amountAlreadyRefunded: rows.reduce((s, r) => s + Number(r.amount ?? 0), 0),
    };
  });
}

function shippingAlreadyRefunded(refunds: RefundRow[]) {
  return acceptedRefunds(refunds).reduce((s, r) => s + Number(r.shipping_amount ?? 0), 0);
}

interface BalanceInfo {
  on_hand: number;
  allocated: number;
  available: number;
}

const formatAddress = (a: Record<string, string> | null | undefined): string =>
  a ? [a.address, a.address_line1, a.address_line2, a.city, a.state, a.postal_code, a.country].filter(Boolean).join(", ") : "";

export default function SalesOrderDetail() {
  const { id } = useParams();
  const { org, role } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [fulfilOpen, setFulfilOpen] = useState(false);
  const [fulfilQty, setFulfilQty] = useState<Record<string, number>>({});
  const [fulfilLoc, setFulfilLoc] = useState<Record<string, string>>({});
  const [fulfilIdemKey, setFulfilIdemKey] = useState("");
  const [trackOpen, setTrackOpen] = useState(false);
  const [trackShipmentId, setTrackShipmentId] = useState<string | null>(null);
  const [trackCarrier, setTrackCarrier] = useState("");
  const [trackNumber, setTrackNumber] = useState("");
  const [trackCustom, setTrackCustom] = useState("");
  const [allocOpen, setAllocOpen] = useState(false);
  const [allocQty, setAllocQty] = useState<Record<string, number>>({});
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundQty, setRefundQty] = useState<Record<string, number>>({});
  const [refundIncludeShipping, setRefundIncludeShipping] = useState(false);
  const [refundAdjustment, setRefundAdjustment] = useState("");
  const [refundAdjustmentReason, setRefundAdjustmentReason] = useState("");
  const [refundMethod, setRefundMethod] = useState<CompensationMethod>("original_payment");
  const [refundReason, setRefundReason] = useState("");
  const [refundIdemKey, setRefundIdemKey] = useState("");
  const [refundSaving, setRefundSaving] = useState(false);
  const [syncProviderRefund, setSyncProviderRefund] = useState(false);
  const [refundDisposition, setRefundDisposition] = useState<UnfulfilledRefundDisposition | "">("");
  const [cancelRefundedOpen, setCancelRefundedOpen] = useState<string | null>(null);
  const [keepReservedAck, setKeepReservedAck] = useState<string[]>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [allocError, setAllocError] = useState("");
  const recordedTracking = useRef<{ orgId: string; shipmentId: string; number: string; provider: string; method: string } | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["so-detail", org?.id, id],
    enabled: !!org?.id && !!id,
    queryFn: async () => {
      const [so, items, fulfilments, carriers, inboundCancelRes] = await Promise.all([
        supabase
          .from("sales_orders")
          .select("id, order_number, status, order_date, subtotal, currency, total, notes, source, channel_id, location_id, customer_id, payment_status, provider_cancel_status, provider_cancel_error, provider_cancel_reason, provider_cancelled_at, priority, customer_reference, discount, tax, shipping, billing_address, shipping_address, customers(name), locations(name), connectors(name, provider)")
          .eq("org_id", org!.id)
          .eq("id", id)
          .maybeSingle(),
        supabase
          .from("sales_order_items")
          .select("catalogue_product_identity_id, catalogue_variant_identity_id, id, product_id, variant_id, qty_ordered, qty_allocated, qty_fulfilled, qty_backordered, qty_cancelled, unit_price, total, is_bundle, bundle_parent_item_id, product_name, sku, variant_attributes, line_discount, line_tax, products(name, sku), variants(sku, option_values)")
          .eq("org_id", org!.id)
          .eq("sales_order_id", id)
          .order("created_at"),
        supabase
          .from("fulfilments")
          .select("id, fulfilment_number, status, shipped_at, notes, locations(name)")
          .eq("org_id", org!.id)
          .eq("sales_order_id", id)
          .order("created_at", { ascending: false }),
        supabase
          .from("carrier_services")
          .select("id, name, carrier_code")
          .eq("org_id", org!.id)
          .eq("enabled", true)
          .order("name"),
        supabase
          .from("marketplace_inbound_requests")
          .select("id, request_kind, provider_request_id, initiator, reason, request_state, provider_order_cancel_state, provider_payment_status, provider_fulfillment_status, requested_at, last_checked_at")
          .eq("org_id", org!.id)
          .eq("sales_order_id", id)
          .order("last_checked_at", { ascending: false }),
      ]);
      const returnsRes = await supabase
        .from("sales_returns")
        .select("id, return_number, status, reason, created_at")
        .eq("org_id", org!.id)
        .eq("sales_order_id", id)
        .order("created_at", { ascending: false });
      const refundsRes = await supabase
        .from("sales_refunds")
        .select("id, amount, currency, status, reason, provider_sync_status, refund_mode, shipping_amount, created_at")
        .eq("org_id", org!.id)
        .eq("sales_order_id", id)
        .order("created_at", { ascending: false });
      const refundIds = (refundsRes.data ?? []).map((r) => r.id);
      const refundItemsRes = refundIds.length === 0
        ? { data: [] }
        : await supabase
            .from("sales_refund_items")
            .select("sales_refund_id, sales_order_item_id, sku, qty, amount, qty_unfulfilled_cancelled")
            .eq("org_id", org!.id)
            .in("sales_refund_id", refundIds);
      const externalRes = await supabase
        .from("external_identifiers")
        .select("external_id")
        .eq("org_id", org!.id)
        .eq("entity_type", "sales_order")
        .eq("entity_id", id)
        .maybeSingle();
      const fulfilmentIds = (fulfilments.data ?? []).map((f) => f.id);
      const [fulfilmentItems, shipments] = await Promise.all([
        fulfilmentIds.length === 0
          ? { data: [] }
          : supabase
              .from("fulfilment_items")
              .select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, variant_attributes_snapshot, catalogue_snapshot_origin, id, fulfilment_id, product_id, qty_fulfilled, products(name, sku)")
              .eq("org_id", org!.id)
              .in("fulfilment_id", fulfilmentIds)
              .order("created_at"),
        fulfilmentIds.length === 0
          ? { data: [] }
          : supabase
              .from("shipments")
              .select("id, fulfilment_id, carrier_service_id, status, tracking_number, shipped_at, delivered_at, carrier_reference, idempotency_key, tracking_status, service_name, shipping_provider, provider_sync_status, provider_sync_error, provider_shipment_id, label_url, carrier_services(name)")
              .eq("org_id", org!.id)
              .in("fulfilment_id", fulfilmentIds)
              .order("created_at", { ascending: false }),
      ]);
      return {
        so: so.data
          ? ({
              ...so.data,
              customers: firstRelation(so.data.customers),
              locations: firstRelation(so.data.locations),
              connectors: firstRelation(so.data.connectors),
            } as SoRow)
          : null,
        items: (items.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          variants: firstRelation(row.variants),
        })) as SoItemRow[],
        fulfilments: (fulfilments.data ?? []).map((row) => ({
          ...row,
          locations: firstRelation(row.locations),
        })) as FulfilmentRow[],
        carriers: (carriers.data ?? []) as CarrierRow[],
        fulfilmentItems: (fulfilmentItems.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
        })) as FulfilmentItemRow[],
        shipments: (shipments.data ?? []).map((row) => ({
          ...row,
          carrier_services: firstRelation(row.carrier_services),
        })) as ShipmentRow[],
        returns: (returnsRes.data ?? []) as ReturnRow[],
        refunds: (refundsRes.data ?? []) as RefundRow[],
        refundItems: (refundItemsRes.data ?? []) as RefundItemRow[],
        externalOrderId: (externalRes.data as { external_id: string } | null)?.external_id ?? null,
        inboundEvents: ((inboundCancelRes.data ?? []) as Array<{
          id: string;
          request_kind: string;
          provider_request_id: string;
          initiator: string | null;
          reason: string | null;
          request_state: string | null;
          provider_order_cancel_state: string | null;
          provider_payment_status: string | null;
          provider_fulfillment_status: string | null;
          requested_at: string | null;
          last_checked_at: string | null;
        }>),
      };
    },
  });

  const { data: availability, isSuccess: availabilityReady } = useQuery({
    queryKey: ["so-availability", org?.id, data?.so?.id],
    enabled: !!org?.id && !!data?.so && shouldLoadOrderAvailability(data.so.location_id),
    queryFn: async () => {
      const [balancesRes, locsRes] = await Promise.all([
        supabase
          .from("inventory_balances")
          .select("product_id, variant_id, location_id, on_hand, allocated, available")
          .eq("org_id", org!.id),
        supabase.from("locations").select("id, name").eq("org_id", org!.id).order("name"),
      ]);
      const map: Record<string, BalanceInfo> = {};
      (balancesRes.data ?? []).forEach((b) => {
        map[balanceKey(b.product_id, b.variant_id, b.location_id)] = { on_hand: b.on_hand, allocated: b.allocated, available: b.available };
      });
      return { map, locations: (locsRes.data ?? []) as { id: string; name: string }[] };
    },
  });

  const recommendedLocationsForOrder = () => {
    return seedLineFulfilmentLocations({
      items: (data?.items ?? []).filter((it) => !it.is_bundle),
      locations: availability?.locations ?? [],
      balances: availability?.map ?? {},
      orderLocationId: data?.so?.location_id,
    });
  };

  const openFulfil = () => {
    const seeded = recommendedLocationsForOrder();
    setFulfilQty(seeded.qtyByLineId);
    setFulfilLoc(seeded.locByLineId);
    setFulfilIdemKey(crypto.randomUUID());
    setError("");
    setFulfilOpen(true);
  };

  useEffect(() => {
    if (!fulfilOpen || !availabilityReady || !availability) return;
    const seeded = recommendedLocationsForOrder();
    setFulfilLoc((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [lineId, locationId] of Object.entries(seeded.locByLineId)) {
        if (!next[lineId]) {
          next[lineId] = locationId;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fill empty line locations once balances arrive
  }, [fulfilOpen, availabilityReady, availability]);

  const openAllocate = () => {
    const initial: Record<string, number> = {};
    const soLoc = data?.so?.location_id ?? "";
    (data?.items ?? []).filter((it) => !it.is_bundle).forEach((it) => {
      const remaining = remainingFulfilmentQty(it.qty_ordered, it.qty_cancelled ?? 0, it.qty_fulfilled) - it.qty_allocated;
      if (remaining > 0) {
        const avail = it.product_id
          ? availability?.map[balanceKey(it.product_id, it.variant_id, soLoc)]?.available ?? 0
          : 0;
        initial[it.id] = Math.min(remaining, Math.max(avail, 0));
      }
    });
    setAllocQty(initial);
    setAllocError("");
    setAllocOpen(true);
  };

  const handleAllocate = async () => {
    setAllocError("");
    setSaving(true);
    const pLines = (data?.items ?? [])
      .filter((it) => !it.is_bundle && it.product_id && (allocQty[it.id] ?? 0) > 0)
      .map((it) => ({ product_id: it.product_id as string, variant_id: it.variant_id, qty: allocQty[it.id] }));
    if (pLines.length === 0) {
      setSaving(false);
      setAllocError("Enter a quantity to allocate for at least one line.");
      return;
    }
    try {
      await salesApi.allocateSalesOrder(id!, pLines);
    } catch (e) {
      setSaving(false);
      setAllocError((e as Error).message);
      return;
    }
    setSaving(false);
    setAllocOpen(false);
    queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["so-availability", org?.id, data?.so?.id] });
    queryClient.invalidateQueries({ queryKey: ["sales-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const handleCancel = async () => {
    setError("");
    const current = data?.so;
    const marketplace = (current?.connectors?.provider ?? current?.source ?? "").toLowerCase();
    const needsEbayReason = marketplace === "ebay";
    if (needsEbayReason && !isEbayCancelReasonCode(cancelReason)) {
      setError(ORDER_CANCEL_COPY.reasonRequired);
      return;
    }
    setSaving(true);
    try {
      await salesApi.cancelSalesOrder(id!);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      setCancelOpen(false);
      return;
    }
    const staysLocal = remainingOrderCancelMustStayLocal({
      hasConfirmedProviderRefund: (data?.refunds ?? []).some((r) => r.provider_sync_status === "confirmed"),
      paymentStatus: current?.payment_status,
    });
    if (!staysLocal && current?.channel_id && org?.id && (marketplace === "ebay" || marketplace === "bigcommerce")) {
      const synced = await syncMarketplaceCancel({
        connectorId: current.channel_id,
        orgId: org.id,
        salesOrderId: id!,
        cancelReason: needsEbayReason ? cancelReason : undefined,
      });
      if (!synced.success) {
        setSaving(false);
        setCancelOpen(false);
        setError(synced.error || ORDER_CANCEL_COPY.localDoneProviderPending);
        queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
        queryClient.invalidateQueries({ queryKey: ["sales-orders", org?.id] });
        queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
        queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
        return;
      }
    }
    setSaving(false);
    setCancelOpen(false);
    setCancelReason("");
    queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["sales-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const openRefund = () => {
    setRefundQty({});
    setRefundIncludeShipping(false);
    setRefundAdjustment("");
    setRefundAdjustmentReason("");
    setRefundMethod("original_payment");
    setRefundReason("");
    setRefundIdemKey(crypto.randomUUID());
    setSyncProviderRefund(false);
    setRefundDisposition("");
    setError("");
    setRefundOpen(true);
  };

  const handleRefund = async () => {
    if (!org?.id || !id || !data?.so) return;
    const lines = refundLineStates(data.items, data.refunds ?? [], data.refundItems ?? []);
    const already = acceptedRefunds(data.refunds ?? []).reduce((s, r) => s + Number(r.amount ?? 0), 0);
    const quote = composeCompensation({
      chargedTotal: data.so.total,
      alreadyRefunded: already,
      tax: data.so.tax,
      orderShipping: data.so.shipping,
      shippingAlreadyRefunded: shippingAlreadyRefunded(data.refunds ?? []),
      lines,
      selections: Object.entries(refundQty).map(([salesOrderItemId, qty]) => ({ salesOrderItemId, qty })),
      includeShipping: refundIncludeShipping,
      adjustmentAmount: refundAdjustment,
      adjustmentReason: refundAdjustmentReason,
      method: refundMethod,
      provider: data.so.connectors?.provider ?? data.so.source,
    });
    if (quote.ok === false) {
      setError(quote.message);
      return;
    }
    const unfulfilledRefundQty = quote.quote.lines.reduce((sum, line) => {
      const item = data.items.find((it) => it.id === line.salesOrderItemId);
      if (!item) return sum;
      return sum + refundedUnfulfilledCancellableQty({
        qtyOrdered: item.qty_ordered,
        qtyFulfilled: item.qty_fulfilled,
        qtyCancelled: item.qty_cancelled ?? 0,
        qtyRefunded: line.qty,
        qtyAlreadyCancelledFromRefund: 0,
      });
    }, 0);
    if (requiresUnfulfilledDisposition(unfulfilledRefundQty) && (refundDisposition === "" || refundDisposition == null)) {
      setError("Choose whether to keep the refunded quantity on the order or cancel it from fulfilment.");
      return;
    }
    setRefundSaving(true);
    setError("");
    try {
      const refundId = await returnsApi.createSalesRefund({
        salesOrderId: id,
        returnId: null,
        amount: quote.quote.mode === "order_level" ? quote.quote.amount : null,
        reason: refundReason || refundAdjustmentReason || null,
        idempotencyKey: refundIdemKey,
        mode: quote.quote.mode,
        lines: quote.quote.lines.map((l) => ({ salesOrderItemId: l.salesOrderItemId, qty: l.qty })),
        shippingAmount: quote.quote.shipping,
        adjustmentAmount: quote.quote.adjustment,
      });
      if (refundDisposition === "refund_and_cancel_unfulfilled" && refundId) {
        await returnsApi.cancelRefundedUnfulfilledQuantity(refundId);
      }
      if (syncProviderRefund && (data.so.source === "bigcommerce" || data.so.source === "ebay") && data.so.channel_id) {
        const sync = await syncMarketplaceRefund({
          connectorId: data.so.channel_id,
          orgId: org.id,
          salesOrderId: id,
          refundId,
        });
          if (!sync.success) {
            setRefundSaving(false);
            setError(sync.error || "Refund saved in Tradivo. Provider refund needs retry.");
            queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
            return;
          }
      }
    } catch (e) {
      setRefundSaving(false);
      setError((e as Error).message);
      return;
    }
    setRefundSaving(false);
    setRefundOpen(false);
    queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["sales-orders", org?.id] });
  };

  const handleRetryProviderRefund = async (refundId: string) => {
    if (!org?.id || !id || !data?.so?.channel_id) return;
    setRefundSaving(true);
    setError("");
    const sync = await syncMarketplaceRefund({
      connectorId: data.so.channel_id,
      orgId: org.id,
      salesOrderId: id,
      refundId,
    });
    setRefundSaving(false);
    if (!sync.success) {
      setError(sync.error || "Provider refund needs retry.");
    }
    queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
  };

  const handleRetryProviderCancel = async () => {
    if (!org?.id || !id || !data?.so?.channel_id) return;
    const marketplace = (data.so.connectors?.provider ?? data.so.source ?? "").toLowerCase();
    const reason = data.so.provider_cancel_reason ?? cancelReason;
    if (marketplace === "ebay" && !isEbayCancelReasonCode(reason)) {
      setError(ORDER_CANCEL_COPY.reasonRequired);
      setCancelOpen(true);
      return;
    }
    setSaving(true);
    setError("");
    const sync = await syncMarketplaceCancel({
      connectorId: data.so.channel_id,
      orgId: org.id,
      salesOrderId: id,
      cancelReason: marketplace === "ebay" ? reason : undefined,
    });
    setSaving(false);
    if (!sync.success) setError(sync.error || ORDER_CANCEL_COPY.localDoneProviderPending);
    queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
  };

  const handleCancelRefundedUnfulfilled = async (refundId: string) => {
    setRefundSaving(true);
    setError("");
    try {
      await returnsApi.cancelRefundedUnfulfilledQuantity(refundId);
    } catch (e) {
      setRefundSaving(false);
      setError((e as Error).message);
      return;
    }
    setRefundSaving(false);
    setCancelRefundedOpen(null);
    queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["sales-orders", org?.id] });
  };

  const handleReallocate = async () => {
    setError("");
    setSaving(true);
    try {
      await salesApi.reallocateBackorderedItems(org!.id);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);
    queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["so-availability", org?.id, data?.so?.id] });
    queryClient.invalidateQueries({ queryKey: ["sales-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const handleFulfil = async () => {
    setError("");
    const built = buildFulfilmentRequest({
      items: data?.items ?? [],
      qtyByLineId: fulfilQty,
      locByLineId: fulfilLoc,
      orderLocationId: data?.so?.location_id,
      balances: availability?.map ?? {},
    });
    if (built.ok === false) {
      if (built.code === "no_qty") setError("Enter a quantity to fulfil for at least one line.");
      else if (built.code === "no_location") setError("Select a fulfilment location with available stock before confirming.");
      else setError("There isn't enough stock available to complete this. Check the quantities and try again.");
      return;
    }
    setSaving(true);
    try {
      await salesApi.fulfilSalesOrder({
        soId: id!,
        locationId: built.fallbackLocationId,
        lines: built.lines,
        idempotencyKey: fulfilIdemKey || undefined,
      });
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);
    setFulfilOpen(false);
    queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["so-availability", org?.id, data?.so?.id] });
    queryClient.invalidateQueries({ queryKey: ["sales-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const handlePrintKind = async (kind: OrderDocumentKind) => {
    if (!org?.id || !id) return;
    setError("");
    try {
      await printSalesOrderDocuments(org.id, [id], kind);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const retryShipmentChannelSync = async (shipmentId: string) => {
    if (!org?.id || !id || !data?.so) return;
    setError("");
    setSaving(true);
    try {
      if (data.so.source === "bigcommerce" && data.so.channel_id) {
        const sync = await syncBigCommerceFulfilment({
          connectorId: data.so.channel_id,
          orgId: org.id,
          salesOrderId: id,
          shipmentId,
        });
        if (!sync.success) throw new Error(sync.error || "Tracking saved in Tradivo. Channel sync could not start. Retry shipment sync.");
      } else if (data.so.source === "ebay" && data.so.channel_id) {
        const sync = await syncEbayFulfilment({
          connectorId: data.so.channel_id,
          orgId: org.id,
          salesOrderId: id,
          shipmentId,
        });
        if (!sync.success) throw new Error(sync.error || "Tracking saved in Tradivo. Channel sync could not start. Retry shipment sync.");
      }
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
      return;
    }
    setSaving(false);
    queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
  };

  const openTracking = (shipmentId: string, existing?: ShipmentRow) => {
    recordedTracking.current = null;
    setTrackShipmentId(shipmentId);
    setTrackNumber(existing?.tracking_number ?? "");
    const carrier = restoreManualTrackingCarrier(existing);
    setTrackCarrier(carrier.code);
    setTrackCustom(carrier.custom);
    setError("");
    setTrackOpen(true);
  };

  const handleCreateShipment = async (f: FulfilmentRow) => {
    setError("");
    setSaving(true);
    let createdId: string;
    try {
      createdId = await warehouseApi.createShipmentHeader(f.id);
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);
    queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["shipments", org?.id] });
    openTracking(createdId);
  };

  const handleSaveTracking = async () => {
    if (!trackShipmentId || !org?.id || !id) return;
    const tracking = trackNumber.trim();
    if (!tracking) {
      setError("Enter the tracking number.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const existing = (data?.shipments ?? []).find((s) => s.id === trackShipmentId);
      const receipt = recordedTracking.current;
      const carrier = trackCarrier && (trackCarrier !== "other" || trackCustom.trim())
        ? mapCarrierToBcShippingProvider(trackCarrier, trackCustom) : null;
      const acknowledged = receipt?.orgId === org.id && receipt.shipmentId === trackShipmentId;
      // The latest local acknowledgement takes precedence over a lagging query.
      const sameRecord = acknowledged
        ? !!carrier && receipt.number === tracking && receipt.provider === carrier.shipping_provider && receipt.method === carrier.shipping_method
        : storedManualTrackingMatches(existing, tracking, trackCarrier, trackCustom);
      if (sameRecord && !acknowledged && existing?.provider_sync_status === "confirmed") {
        setTrackOpen(false);
        setSaving(false);
        return;
      }
      if (!sameRecord) {
        if (trackCarrier === "other" && !trackCustom.trim()) throw new Error("Enter the custom carrier name.");
        if (!carrier) throw new Error("Select a carrier before saving tracking.");
        const recorded = await warehouseApi.setShipmentTracking(
          trackShipmentId,
          tracking,
          trackCarrier,
          trackCarrier === "other" ? trackCustom : "Australia Post",
        );
        // Keep this acknowledged save while the modal retries channel sync.
        // A lagging query must not turn that retry into another local write.
        recordedTracking.current = { orgId: org.id, shipmentId: trackShipmentId, number: recorded.tracking_number,
          provider: carrier.shipping_provider, method: carrier.shipping_method };
      }
      if (data?.so?.source === "bigcommerce" && data.so.channel_id) {
        const sync = await syncBigCommerceFulfilment({
          connectorId: data.so.channel_id,
          orgId: org.id,
          salesOrderId: id,
          shipmentId: trackShipmentId,
        });
        if (!sync.success) {
          setSaving(false);
          setError(sync.error || "Tracking saved in Tradivo. Channel sync could not start. Retry shipment sync.");
          queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
          return;
        }
      }
      if (data?.so?.source === "ebay" && data.so.channel_id) {
        const sync = await syncEbayFulfilment({
          connectorId: data.so.channel_id,
          orgId: org.id,
          salesOrderId: id,
          shipmentId: trackShipmentId,
        });
        if (!sync.success) {
          setSaving(false);
          setError(sync.error || "Tracking saved in Tradivo. Channel sync could not start. Retry shipment sync.");
          queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
          return;
        }
      }
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);
    setTrackOpen(false);
    queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
    queryClient.invalidateQueries({ queryKey: ["shipments", org?.id] });
  };

  const handleEditOrder = async (values: SalesOrderEditValues) => {
    if (!id) return;
    setError("");
    setSaving(true);
    try {
      await salesApi.editSalesOrder({
        soId: id,
        shippingAddress: addressToJson(values.shipping),
        billingAddress: addressToJson(values.billing),
        priority: values.priority,
        customerReference: values.customerReference.trim() || null,
        notes: values.notes,
      });
      setEditOpen(false);
      queryClient.invalidateQueries({ queryKey: ["so-detail", org?.id, id] });
      queryClient.invalidateQueries({ queryKey: ["shipments", org?.id] });
    } catch (e) {
      setError((e as Error).message);
    }
    setSaving(false);
  };

  if (isLoading) return <LoadingState />;

  if (isError || !data?.so) {
    return (
      <div>
        <PageHeader title="Sales Order" backTo="/sales-orders" />
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load this sales order.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const so = data.so;
  const provenance = buildProvenance({ source: so.source, connectorProvider: so.connectors?.provider ?? null, connectorName: so.connectors?.name ?? null, externalOrderId: data.externalOrderId ?? null });
  const physicalItems = data.items.filter((it) => !it.is_bundle);
  const fulfilledCount = physicalItems.reduce((s, it) => s + it.qty_fulfilled, 0);
  const allocatedCount = physicalItems.reduce((s, it) => s + it.qty_allocated, 0);
  const orderedCount = physicalItems.reduce((s, it) => s + it.qty_ordered, 0);
  const canFulfil = FULFILLABLE_STATUSES.includes(so.status);
  const canAllocate = ALLOCATABLE_STATUSES.includes(so.status);
  const canCancel = isCancellable(so.status);
  const allowedSales = hasRole(role, "operations", "sales");
  const allowedFulfil = hasRole(role, "operations", "warehouse", "sales");
  const canManageOrder = canManageSalesOrders(role);
  const linesEditable = canEditSalesOrderLines(so.status);
  const showEditOrder = shouldShowEditOrderCta(canManageOrder, so.status, data.shipments);
  const postFulfilment = so.status === "fulfilled" || fulfilledCount > 0;
  const canReturn = canOperateInventory(role);
  const alreadyReturnedQty = 0;
  const showCreateReturn = canReturn && canCreateSalesReturn({ qtyFulfilled: fulfilledCount, qtyAlreadyReturned: alreadyReturnedQty });
  const cancelledCount = physicalItems.reduce((s, it) => s + Number(it.qty_cancelled ?? 0), 0);
  const remainingToFulfil = physicalItems.reduce((s, it) => s + remainingFulfilmentQty(it.qty_ordered, it.qty_cancelled ?? 0, it.qty_fulfilled), 0);
  const refundedQty = (data.refundItems ?? []).reduce((s, ri) => s + Number(ri.qty ?? 0), 0);
  const channelRefundConfirmed = (data.refunds ?? []).some((r) => r.provider_sync_status === "confirmed");
  const completedReturn = hasCompletedSalesReturn(data.returns ?? []);
  const statusLayers = orderPaymentChannelLayers({
    orderStatus: so.status,
    paymentStatus: so.payment_status,
    channelName: provenance.provider ?? so.source,
    confirmedChannelRefund: channelRefundConfirmed,
    providerCancelStatus: so.provider_cancel_status,
  });
  const inboundEvents = data.inboundEvents ?? [];
  const inboundCancel = inboundEvents.find((row) => row.request_kind === "buyer_cancellation") ?? null;
  const afterSalesRows = inboundAfterSalesUiRows(inboundEvents);
  const fulfilmentSplit = marketplaceFulfilmentSplit({
    localOrderStatus: so.status,
    qtyFulfilled: fulfilledCount,
    providerFulfillmentStatus: inboundCancel?.provider_fulfillment_status,
    inboundRequestState: inboundCancel?.request_state,
    inboundInitiator: inboundCancel?.initiator,
    inboundOrderCancelState: inboundCancel?.provider_order_cancel_state,
  });
  const orderAttention = buildSalesOrderAttentionItems({
    provider: provenance.provider ?? so.source,
    refunds: data.refunds ?? [],
    refundItems: data.refundItems ?? [],
    items: physicalItems,
    acknowledgedKeepReservedIds: keepReservedAck,
    orderStatus: so.status,
    providerCancelStatus: so.provider_cancel_status,
    inboundCancelId: inboundCancel?.provider_request_id,
    inboundInitiator: inboundCancel?.initiator,
    inboundReason: inboundCancel?.reason,
    inboundRequestState: inboundCancel?.request_state,
    inboundProviderOrderCancelState: inboundCancel?.provider_order_cancel_state,
    inboundProviderFulfillmentStatus: inboundCancel?.provider_fulfillment_status,
    inboundEvents,
  });
  const refundedAmount = (data.refunds ?? []).filter((r) => r.status !== "rejected" && r.status !== "failed").reduce((s, r) => s + Number(r.amount ?? 0), 0);
  const refundExposure = composeRefundExposure({ chargedTotal: so.total, alreadyRefunded: refundedAmount });
  const refundLines = refundLineStates(physicalItems, data.refunds ?? [], data.refundItems ?? []);
  const remainingCancelLines = remainingCancelLinePreviews(
    physicalItems.map((it) => ({
      id: it.id,
      productName: historicalProductName(it),
      options: lineVariantLabel(historicalVariantOptions(it), variantLabel(historicalVariantOptions(it))),
      sku: historicalSku(it),
      qtyOrdered: it.qty_ordered,
      qtyCancelled: it.qty_cancelled ?? 0,
      qtyFulfilled: it.qty_fulfilled,
      qtyAllocated: it.qty_allocated,
      qtyRefunded: refundLines.find((line) => line.salesOrderItemId === it.id)?.qtyAlreadyRefunded ?? 0,
    })),
  );
  const remainingCancelStaysLocal = remainingOrderCancelMustStayLocal({
    hasConfirmedProviderRefund: channelRefundConfirmed,
    paymentStatus: so.payment_status,
  });
  const cancelNeedsEbayReason = !remainingCancelStaysLocal && (so.connectors?.provider ?? so.source) === "ebay";
  const currentRefundQuote = composeCompensation({
    chargedTotal: so.total,
    alreadyRefunded: refundedAmount,
    tax: so.tax,
    orderShipping: so.shipping,
    shippingAlreadyRefunded: shippingAlreadyRefunded(data.refunds ?? []),
    lines: refundLines,
    selections: Object.entries(refundQty).map(([salesOrderItemId, qty]) => ({ salesOrderItemId, qty })),
    includeShipping: refundIncludeShipping,
    adjustmentAmount: refundAdjustment,
    adjustmentReason: refundAdjustmentReason,
    method: refundMethod,
    provider: so.connectors?.provider ?? so.source,
  });
  const previewQuote = currentRefundQuote.ok ? currentRefundQuote.quote : null;
  const unfulfilledRefundPreviewQty = previewQuote
    ? previewQuote.lines.reduce((sum, line) => {
        const item = physicalItems.find((it) => it.id === line.salesOrderItemId);
        if (!item) return sum;
        return sum + refundedUnfulfilledCancellableQty({
          qtyOrdered: item.qty_ordered,
          qtyFulfilled: item.qty_fulfilled,
          qtyCancelled: item.qty_cancelled ?? 0,
          qtyRefunded: line.qty,
          qtyAlreadyCancelledFromRefund: 0,
        });
      }, 0)
    : 0;
  const showRefund = allowedSales && canRefundSalesOrder({ paymentStatus: so.payment_status, chargedTotal: so.total, alreadyRefunded: refundedAmount });
  const hasBackordered = physicalItems.some((it) => (it.qty_backordered ?? 0) > 0);
  const residualLabel = residualQtyLabel(allocatedCount, fulfilledCount);
  const fulfilConfirmDisabled = fulfilmentConfirmDisabled({
    saving,
    availabilityReady,
    items: physicalItems,
    qtyByLineId: fulfilQty,
    locByLineId: fulfilLoc,
    orderLocationId: so.location_id,
    balances: availability?.map ?? {},
  });

  const showFulfil = canFulfil && allowedFulfil;
  const showAllocate = canAllocate && allowedSales;
  const primaryAllocate = showAllocate && shouldPromoteAllocateCta(so.status, physicalItems);
  const primaryFulfil = showFulfil && !primaryAllocate;
  const soMenuItems: ActionMenuItem[] = [
    ...(showFulfil && !primaryFulfil ? [{ key: "fulfil", label: "Fulfil order", icon: "ri-truck-line", onClick: openFulfil }] : []),
    ...(showAllocate && !primaryAllocate ? [{ key: "allocate", label: "Allocate stock", icon: "ri-lock-line", onClick: openAllocate }] : []),
    ...(showRefund ? [{ key: "refund", label: "Refund", icon: "ri-refund-2-line", onClick: openRefund }] : []),
    ...(showCreateReturn ? [{ key: "return", label: "Create return", icon: "ri-arrow-go-back-line", onClick: () => navigate(`/returns?orderId=${so.id}`) }] : []),
    ...(canCancel && allowedSales ? [{ key: "cancel", label: "Cancel order", icon: "ri-close-circle-line", onClick: () => setCancelOpen(true), destructive: true }] : []),
  ];

  return (
    <div>
      <PageHeader title={so.order_number} subtitle={so.customers?.name ?? "—"} backTo="/sales-orders">
        <div className="flex items-center gap-2">
          {primaryFulfil && (
            <button
              onClick={openFulfil}
              aria-label="Fulfil Order"
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-truck-line" /> Fulfil Order
            </button>
          )}
          {primaryAllocate && (
            <button
              onClick={openAllocate}
              aria-label="Allocate Stock"
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-lock-line" /> Allocate Stock
            </button>
          )}
          {showEditOrder && (
            <button
              type="button"
              onClick={() => {
                setError("");
                setEditOpen(true);
              }}
              aria-label="Edit Order"
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-pencil-line" /> Edit Order
            </button>
          )}
          {hasBackordered && allowedSales && (
            <button
              onClick={handleReallocate}
              disabled={saving}
              className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-refresh-line" /> {saving ? "Reallocating..." : "Reallocate backorders"}
            </button>
          )}
          {canPrintOrderDocuments(role) && (
            <PrintDocumentsMenu
              mode="single"
              includeLabel
              labelAvailable={shippingLabelPrintState(data.shipments).available}
              onPrint={(kind) => void handlePrintKind(kind)}
              onShippingLabel={() => {
                const state = shippingLabelPrintState(data.shipments);
                if (state.available) {
                  window.open(state.url, "_blank", "noopener,noreferrer");
                  return;
                }
                setError(state.label);
              }}
            />
          )}
          {soMenuItems.length > 0 && <ActionMenu label="More" items={soMenuItems} />}
        </div>
      </PageHeader>

      {error && <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

      {orderAttention.length > 0 && (
        <div className="mb-4 space-y-3" id="order-attention" data-testid="order-attention-panel">
          {orderAttention.map((item) => (
            <OrderAttentionCard
              key={item.id}
              testId={item.id}
              title={item.title}
              body={item.body}
              icon={item.icon}
              tone={item.tone}
              primaryLabel={item.primaryLabel}
              primaryDestructive={item.primaryDestructive}
              primaryLoading={refundSaving}
              onPrimary={() => {
                if (item.kind === "buyer_cancellation" || item.kind === "buyer_return" || item.kind === "buyer_inr" || item.kind === "buyer_snad" || item.kind === "ebay_case" || item.kind === "payment_dispute") {
                  navigate("/integrations/exceptions");
                  return;
                }
                if (item.kind === "cancellation_sync") {
                  void handleRetryProviderCancel();
                  return;
                }
                if (!item.refundId) return;
                if (item.kind === "refund_sync") void handleRetryProviderRefund(item.refundId);
                if (item.kind === "cancel_refunded_unfulfilled") setCancelRefundedOpen(item.refundId);
              }}
              secondaryLabel={item.secondaryLabel}
              onSecondary={item.refundId && item.secondaryLabel
                ? () => setKeepReservedAck((ids) => ids.includes(item.refundId!) ? ids : [...ids, item.refundId!])
                : undefined}
            />
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Order summary with backorder state */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge status={so.status} />
                <span className="text-xs text-slate-500">Order: {statusLayers.orderLabel}</span>
                <span className="text-sm text-slate-500">Ordered {fmtDate(so.order_date)}</span>
                <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full font-medium ${provenance.isMarketplace ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                  <i className={provenance.isMarketplace ? "ri-store-2-line" : "ri-file-list-3-line"} />
                  {provenance.isMarketplace ? provenanceLabel(provenance) : "Manual order"}
                </span>
                {provenance.isMarketplace && so.channel_id && (
                  <button onClick={() => navigate("/integrations")} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium whitespace-nowrap inline-flex items-center gap-1">
                    <i className="ri-store-2-line" /> View channel
                  </button>
                )}
                <button onClick={() => navigate("/integrations/exceptions")} className="text-xs text-slate-500 hover:text-slate-700 font-medium whitespace-nowrap inline-flex items-center gap-1">
                  <i className="ri-alert-line" /> Exceptions
                </button>
                {so.priority && so.priority !== "normal" && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold capitalize ${so.priority === "urgent" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                    {so.priority}
                  </span>
                )}
                {so.payment_status && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${so.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : so.payment_status === "refunded" ? "bg-slate-100 text-slate-700" : "bg-amber-100 text-amber-700"}`}>
                    Payment: {statusLayers.paymentLabel}
                  </span>
                )}
                {provenance.isMarketplace && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-700">
                    Channel: {statusLayers.channelLabel}
                  </span>
                )}
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-900">{money(so.total, so.currency)}</p>
                <div className="text-xs text-slate-400 mt-1 space-y-0.5">
                  <div>Charged: {money(refundExposure.charged, so.currency)}</div>
                  <div>Refunded: {money(refundExposure.refunded, so.currency)}</div>
                  <div>Net retained: {money(refundExposure.netRetained, so.currency)}</div>
                  <div className="text-slate-500 pt-1">Order: {statusLayers.orderLabel}</div>
                  <div>Payment: {statusLayers.paymentLabel}</div>
                  {provenance.isMarketplace && <div>Channel: {statusLayers.channelLabel}</div>}
                  {so.provider_cancel_status && (
                    <div>Marketplace cancel: {so.provider_cancel_status === "confirmed" ? channelCancellationConfirmedLabel((provenance.provider ?? so.source) || "Channel") : so.provider_cancel_status === "pending" ? "pending confirmation" : so.provider_cancel_status === "error" ? "not confirmed" : so.provider_cancel_status}</div>
                  )}
                  {afterSalesRows.length > 0 && (
                    <div className="text-amber-700 pt-1 space-y-0.5" data-testid="marketplace-after-sales">
                      <div className="font-medium text-slate-600">Marketplace after-sales</div>
                      <div>Tradivo fulfilment: {fulfilmentSplit.tradivoFulfilment}</div>
                      <div>eBay fulfilment: {fulfilmentSplit.ebayFulfilment}</div>
                      {afterSalesRows.map((row) => (
                        <div key={`${row.kind}:${row.id}`}>
                          {row.label}: {row.state}{row.actionable ? " / AWAITING SELLER ACTION" : ""}
                          {" "}· {row.id}
                        </div>
                      ))}
                    </div>
                  )}
                  {so.status === "cancelled" && so.provider_cancel_status === "confirmed" && so.payment_status === "paid" && !channelRefundConfirmed && (
                    <div className="text-slate-500 pt-1 max-w-xs">{CHANNEL_CANCEL_REVERSAL_COPY}</div>
                  )}
                  {so.provider_cancel_error && <div className="text-red-600">{so.provider_cancel_error}</div>}
                  {provenance.isMarketplace && (
                    <div className="text-slate-500 pt-1 max-w-xs">{MARKETPLACE_STATUS_INDEPENDENCE_COPY}</div>
                  )}
                  <div>{operationalQtyCaption({ ordered: orderedCount, refundedQty, cancelled: cancelledCount, remainingToFulfil })}</div>
                  <div>Subtotal: {money(so.subtotal, so.currency)}</div>
                  {Number(so.discount ?? 0) > 0 && <div>Discount: -{money(so.discount, so.currency)}</div>}
                  {Number(so.tax ?? 0) > 0 && <div>Tax (reporting): {money(so.tax, so.currency)}</div>}
                  {Number(so.shipping ?? 0) > 0 && <div>Shipping: {money(so.shipping, so.currency)}</div>}
                  {so.currency && <div className="text-slate-300">({so.currency})</div>}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 pt-4 border-t border-slate-100">
              <div>
                <p className="text-xs text-slate-400">Customer</p>
                {so.customer_id ? (
                  <Link to={`/customers/${so.customer_id}`} className="text-sm font-medium text-slate-800 hover:text-emerald-700">{so.customers?.name ?? "—"}</Link>
                ) : (
                  <p className="text-sm font-medium text-slate-800">{so.customers?.name ?? "—"}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-400">Fulfilment location</p>
                {so.location_id ? (
                  <Link to={`/locations/${so.location_id}`} className="text-sm font-medium text-slate-800 hover:text-emerald-700">{so.locations?.name ?? "—"}</Link>
                ) : (
                  <p className="text-sm font-medium text-slate-800">{so.locations?.name ?? "—"}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-400">Allocated</p>
                <p className="text-sm font-medium text-emerald-700">{allocatedCount} / {orderedCount}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Fulfilled</p>
                <p className="text-sm font-medium text-emerald-700">{fulfilledCount} / {orderedCount}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">{residualLabel}</p>
                <p className={`text-sm font-medium ${residualLabel === "Unallocated" ? "text-slate-700" : "text-amber-700"}`}>{physicalItems.reduce((s, it) => s + (it.qty_backordered ?? 0), 0)} / {orderedCount}</p>
              </div>
            </div>
            {so.customer_reference && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-xs text-slate-400">Customer reference</p>
                <p className="text-sm font-medium text-slate-800">{so.customer_reference}</p>
              </div>
            )}
            <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-slate-400">Ship to</p>
                <p className="text-sm font-medium text-slate-800">{formatAddress(so.shipping_address) || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Bill to</p>
                <p className="text-sm font-medium text-slate-800">{formatAddress(so.billing_address) || "—"}</p>
              </div>
            </div>
            {so.notes && <p className="text-sm text-slate-600 mt-4">{so.notes}</p>}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-800">Line Items</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50/70 border-b border-slate-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Ordered</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Allocated</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Fulfilled</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">{residualLabel}</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Cancelled</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Unit price</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr key={it.id} className="border-b border-slate-50">
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-slate-800">
                          {it.product_id ? (
                            <button onClick={() => navigate(`/products/${it.product_id}`)} className="hover:text-emerald-700 text-left">{historicalProductName(it)}</button>
                          ) : (
                            historicalProductName(it)
                          )}
                          {it.is_bundle && <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold align-middle">Bundle</span>}
                          {lineVariantLabel(historicalVariantOptions(it), it.variants && (variantLabel(it.variants.option_values) || it.variants.sku)) && (
                            <span className="text-slate-500 font-normal ml-1">· {lineVariantLabel(historicalVariantOptions(it), it.variants && (variantLabel(it.variants.option_values) || it.variants.sku))}</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-400 font-mono">{historicalSku(it) ?? lineSku(it.sku, it.variants?.sku ?? it.products?.sku)}</p>
                        {(Number(it.line_discount ?? 0) > 0 || Number(it.line_tax ?? 0) > 0) && (
                          <p className="text-xs text-slate-400 mt-0.5">
                            {Number(it.line_discount ?? 0) > 0 && <>Disc -{money(it.line_discount, so.currency)} </>}
                            {Number(it.line_tax ?? 0) > 0 && <>Tax +{money(it.line_tax, so.currency)}</>}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{it.qty_ordered}</td>
                      <td className="px-4 py-3 text-sm text-emerald-700 text-right font-medium">{it.is_bundle ? <span className="text-slate-300">—</span> : it.qty_allocated}</td>
                      <td className="px-4 py-3 text-sm text-emerald-700 text-right font-medium">{it.is_bundle ? <span className="text-slate-300">—</span> : it.qty_fulfilled}</td>
                      <td className="px-4 py-3 text-sm text-amber-700 text-right font-medium">
                        {it.is_bundle ? <span className="text-slate-300">—</span> : (it.qty_backordered ?? 0) > 0 ? it.qty_backordered : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right font-medium">
                        {it.is_bundle ? <span className="text-slate-300">—</span> : (it.qty_cancelled ?? 0) > 0 ? it.qty_cancelled : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 text-right">{money(it.unit_price, so.currency)}</td>
                      <td className="px-4 py-3 text-sm text-slate-800 text-right font-medium">{money(it.total, so.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-800">Fulfilments & Shipments</h3>
            </div>
            {data.fulfilments.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">Nothing fulfilled yet</div>
            ) : (
              <div className="divide-y divide-slate-50">
                {data.fulfilments.map((f) => {
                  const shipmentsFor = data.shipments.filter((s) => s.fulfilment_id === f.id);
                  const hasActiveShipment = shipmentsFor.some((s) => s.status !== "cancelled");
                  return (
                    <div key={f.id} className="px-4 py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-semibold text-slate-800 font-mono">{f.fulfilment_number}</span>
                          <span className="text-xs text-slate-400 ml-2">{fmtDateTime(f.shipped_at)}</span>
                        </div>
                        <StatusBadge status={f.status} />
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                        {data.fulfilmentItems
                          .filter((fi) => fi.fulfilment_id === f.id)
                          .map((fi) => (
                            <span key={fi.id}>
                              {historicalProductName(fi)} <span className="font-medium text-emerald-700">×{fi.qty_fulfilled}</span>
                            </span>
                          ))}
                      </div>
                      {shipmentsFor.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {shipmentsFor.map((s) => {
                            const soAusPostUrl = isAustraliaPostShipment(s) ? australiaPostTrackingUrl(s.tracking_number) : null;
                            return (
                            <div key={s.id} className="flex flex-col gap-2 text-sm bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                              <button
                                onClick={() => navigate(fulfilmentShipmentPath(s.id))}
                                className="flex items-center gap-3 w-full hover:text-slate-900 transition-colors text-left"
                              >
                                <i className="ri-box-3-line text-slate-400" />
                                <span className="text-slate-600">{s.service_name ?? s.carrier_services?.name ?? "No carrier"}</span>
                                <span className="font-mono text-slate-700">{s.tracking_number ?? "—"}</span>
                                <span className="ml-auto flex items-center gap-1">
                                  <StatusBadge status={s.provider_sync_status === "confirmed" ? "success" : s.provider_sync_status === "error" ? "error" : s.provider_sync_status === "pending" ? "pending" : s.status} />
                                  <i className="ri-arrow-right-s-line text-slate-400" />
                                </span>
                              </button>
                              {soAusPostUrl && (
                                <div className="pl-7 space-y-0.5">
                                  <p className="text-xs text-slate-500">Carrier: Australia Post</p>
                                  <a
                                    href={soAusPostUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700"
                                  >
                                    Track on Australia Post <i className="ri-external-link-line" />
                                  </a>
                                  <p className="text-[11px] text-slate-400">External carrier tracking</p>
                                </div>
                              )}
                              {s.provider_sync_status === "error" && s.provider_sync_error && (
                                <p className="text-xs text-red-600">{s.provider_sync_error}</p>
                              )}
                              {s.tracking_number && s.provider_sync_status !== "confirmed" && !s.provider_sync_error && (
                                <p className="text-xs text-amber-700">{shipmentChannelPendingCopy(data.so.source)}</p>
                              )}
                              {allowedFulfil && (
                                <button
                                  onClick={() => {
                                    if (trackingCta({ trackingNumber: s.tracking_number, providerSyncStatus: s.provider_sync_status }) === "retry") {
                                      void retryShipmentChannelSync(s.id);
                                      return;
                                    }
                                    openTracking(s.id, s);
                                  }}
                                  className="self-start px-3 py-1 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg"
                                >
                                  {trackingCta({ trackingNumber: s.tracking_number, providerSyncStatus: s.provider_sync_status }) === "retry"
                                    ? retryChannelSyncLabel()
                                    : s.tracking_number
                                      ? "Update tracking"
                                      : "Record tracking"}
                                </button>
                              )}
                              {allowedFulfil && trackingCta({ trackingNumber: s.tracking_number, providerSyncStatus: s.provider_sync_status }) === "retry" && (
                                <button onClick={() => openTracking(s.id, s)} disabled={saving}
                                  className="self-start px-3 py-1 bg-white border border-slate-200 text-slate-700 text-xs font-medium rounded-lg">
                                  Update tracking
                                </button>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      )}
                      {!hasActiveShipment && allowedFulfil && (
                        <button
                          onClick={() => handleCreateShipment(f)}
                          disabled={saving}
                          className="mt-3 px-3 py-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                        >
                          <i className="ri-add-line" /> {saving ? "Creating..." : "Create shipment / Record tracking"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Returns</h3>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-400">{data.returns.length}</span>
                {canReturn && (
                  <button
                    onClick={() => navigate(`/returns?orderId=${so.id}`)}
                    className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-add-line" /> New return
                  </button>
                )}
              </div>
            </div>
            {data.returns.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No returns for this order</div>
            ) : (
              <div className="divide-y divide-slate-50">
                {data.returns.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => navigate(`/returns/${r.id}`)}
                    className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors"
                  >
                    <div className="text-left">
                      <span className="text-sm font-medium text-slate-800 font-mono">{r.return_number}</span>
                      {r.reason && <span className="text-xs text-slate-400 ml-2">{r.reason}</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">{fmtDateTime(r.created_at)}</span>
                      <StatusBadge status={r.status} />
                      <i className="ri-arrow-right-s-line text-slate-400" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        open={allocOpen}
        onClose={() => setAllocOpen(false)}
        title={`Allocate Stock — ${so.order_number}`}
        footer={
          <>
            <button onClick={() => setAllocOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={handleAllocate} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Allocating..." : "Reserve Stock"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          {allocError && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{allocError}</div>}
          <p className="text-xs text-slate-500">Stock is reserved from available inventory at the order's fulfilment location. Lines without enough stock will be left partially allocated.</p>
          {data.items.filter((it) => !it.is_bundle).map((it) => {
            const remaining = remainingFulfilmentQty(it.qty_ordered, it.qty_cancelled ?? 0, it.qty_fulfilled) - it.qty_allocated;
            const balance = it.product_id
              ? availability?.map[balanceKey(it.product_id, it.variant_id, data.so.location_id ?? "")]
              : undefined;
            const available = balance?.available ?? 0;
            return (
              <div key={it.id} className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{historicalProductName(it)}</p>
                  <p className="text-xs text-slate-400">
                    {remaining} to allocate · <span className={available > 0 ? "text-emerald-600" : "text-amber-600"}>
                      {available} available
                    </span>
                  </p>
                </div>
                <input
                  type="number"
                  min="0"
                  max={Math.min(remaining, available)}
                  disabled={remaining <= 0 || available <= 0}
                  value={allocQty[it.id] ?? 0}
                  onChange={(e) => setAllocQty((q) => ({ ...q, [it.id]: Number(e.target.value) }))}
                  className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm disabled:opacity-50"
                />
              </div>
            );
          })}
        </div>
      </Modal>

      <FulfilOrderModal
        open={fulfilOpen}
        onClose={() => setFulfilOpen(false)}
        orderNumber={so.order_number}
        orderLocationId={so.location_id}
        items={physicalItems}
        qtyByLineId={fulfilQty}
        locByLineId={fulfilLoc}
        onQtyChange={(lineId, qty) => setFulfilQty((q) => ({ ...q, [lineId]: qty }))}
        onLocChange={(lineId, locationId) => setFulfilLoc((l) => ({ ...l, [lineId]: locationId }))}
        locations={availability?.locations ?? []}
        balances={availability?.map ?? {}}
        error={error}
        saving={saving}
        confirmDisabled={fulfilConfirmDisabled}
        onConfirm={() => void handleFulfil()}
      />

      <Modal
        open={trackOpen}
        onClose={() => setTrackOpen(false)}
        title="Record tracking"
        footer={
          <>
            <button onClick={() => setTrackOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Cancel
            </button>
            <button onClick={handleSaveTracking} disabled={saving || !trackNumber.trim()} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Saving..." : data?.so?.source === "bigcommerce" ? "Save and send" : "Save tracking"}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <p className="text-sm text-slate-600">{shipmentTrackingSavedCopy()}</p>
          <label className="block text-xs font-medium text-slate-500">Carrier</label>
          <select
            value={trackCarrier}
            onChange={(e) => setTrackCarrier(e.target.value)}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">Select carrier (not recorded)</option>
            {MANUAL_SHIPPING_CARRIERS.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
          {trackCarrier === "other" && (
            <input
              type="text"
              value={trackCustom}
              onChange={(e) => setTrackCustom(e.target.value)}
              placeholder="Carrier name"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
            />
          )}
          <label className="block text-xs font-medium text-slate-500">Tracking number</label>
          <input
            type="text"
            value={trackNumber}
            onChange={(e) => setTrackNumber(e.target.value)}
            placeholder="e.g. ABC123456789"
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
          />
        </div>
      </Modal>

      <EditSalesOrderModal
        key={editOpen ? `edit-${so.id}` : "edit-closed"}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        saving={saving}
        error={editOpen ? error : ""}
        postFulfilment={postFulfilment}
        locked={false}
        lockReason=""
        linesLocked={!linesEditable}
        initial={valuesFromOrder(so)}
        onSave={handleEditOrder}
      />

      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title={ORDER_CANCEL_COPY.title}
        footer={
          <>
            <button onClick={() => setCancelOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Back</button>
            <button onClick={() => void handleCancel()} disabled={saving || remainingCancelLines.length === 0 || (cancelNeedsEbayReason && !isEbayCancelReasonCode(cancelReason))} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Saving..." : ORDER_CANCEL_COPY.confirmLabel}
            </button>
          </>
        }
      >
        <div className="space-y-3 text-sm text-slate-700">
          <p>{ORDER_CANCEL_COPY.intro} Cancel {so.order_number} remaining unfulfilled quantity only.</p>
          <ul className="list-disc pl-5 space-y-1 text-xs text-slate-600">
            <li>{ORDER_CANCEL_COPY.releasesAllocation}</li>
            <li>{ORDER_CANCEL_COPY.noPhysicalStock}</li>
            <li>{ORDER_CANCEL_COPY.noRefund}</li>
            <li>{ORDER_CANCEL_COPY.noDouble}</li>
          </ul>
          {remainingCancelStaysLocal && (
            <p className="text-xs text-slate-500">Provider cancellation is not sent. Existing channel refunds stay unchanged.</p>
          )}
          {cancelNeedsEbayReason && (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-slate-500">{ORDER_CANCEL_COPY.reasonLabel}</label>
              <select
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">Select a reason</option>
                {EBAY_CANCEL_REASON_OPTIONS.map((reason) => (
                  <option key={reason.code} value={reason.code}>{reason.label}</option>
                ))}
              </select>
              {ebayCancelReasonIsSellerFault(cancelReason) && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">{ORDER_CANCEL_COPY.sellerFaultWarning}</p>
              )}
              <p className="text-xs text-slate-500">{ORDER_CANCEL_COPY.localDoneProviderPending}</p>
            </div>
          )}
          {remainingCancelLines.length === 0 ? (
            <p className="text-xs text-slate-500">There is no remaining unfulfilled quantity to cancel.</p>
          ) : remainingCancelLines.map((line) => (
            <div key={line.salesOrderItemId} className="border border-slate-200 rounded-lg p-3 space-y-1">
              <div className="font-medium text-slate-900">{line.productName}</div>
              {line.options ? <div className="text-xs text-slate-500">{line.options}</div> : null}
              {line.sku ? <div className="text-xs text-slate-500">SKU {line.sku}</div> : null}
              <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                <div>{ORDER_CANCEL_COPY.qtyRemainingLabel}<div className="font-semibold text-slate-900">{line.qtyRemaining}</div></div>
                <div>{ORDER_CANCEL_COPY.qtyToCancelLabel}<div className="font-semibold text-slate-900">{line.qtyToCancel}</div></div>
                <div>Already cancelled<div className="font-semibold text-slate-900">{line.qtyAlreadyCancelled}</div></div>
                <div>Already refunded<div className="font-semibold text-slate-900">{line.qtyRefunded}</div></div>
              </div>
            </div>
          ))}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!cancelRefundedOpen}
        title="Cancel refunded quantity"
        message="This cancels the refunded unfulfilled quantity on this order and releases its reservation. On-hand quantity will not increase. This is not a return and does not send another channel refund."
        confirmLabel="Cancel refunded quantity"
        loading={refundSaving}
        onCancel={() => setCancelRefundedOpen(null)}
        onConfirm={() => cancelRefundedOpen && void handleCancelRefundedUnfulfilled(cancelRefundedOpen)}
      />

      <Modal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        title={`Refund ${so.order_number}`}
        wide
        footer={
          <>
            <button onClick={() => setRefundOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Back</button>
            <button onClick={() => void handleRefund()} disabled={refundSaving || !previewQuote || (requiresUnfulfilledDisposition(unfulfilledRefundPreviewQty) && refundDisposition === "")} className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {refundSaving ? "Saving..." : "Confirm refund"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {error && refundOpen && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          {completedReturn ? <p className="text-xs text-slate-500">{REFUND_AFTER_RETURN_COPY}</p> : null}
          <div className="flex flex-wrap gap-2">
            <span className="sr-only">Refund items</span>
            <button
              type="button"
              onClick={() => {
                const next: Record<string, number> = {};
                for (const line of refundLines) {
                  next[line.salesOrderItemId] = remainingRefundableQty(line.qtyOrdered, line.qtyAlreadyRefunded);
                }
                setRefundQty(next);
                setRefundIncludeShipping(true);
                setRefundAdjustment("");
              }}
              className="px-3 py-1.5 text-sm rounded-lg border bg-white text-slate-700 border-slate-200"
            >
              Refund remaining order
            </button>
            <button
              type="button"
              onClick={() => {
                setRefundQty({});
                setRefundIncludeShipping(false);
                setRefundAdjustment(refundExposure.refundable > 0 ? String(refundExposure.refundable) : "");
                setRefundAdjustmentReason(refundAdjustmentReason || "Order-level adjustment");
              }}
              className="px-3 py-1.5 text-sm rounded-lg border bg-white text-slate-700 border-slate-200"
            >
              Order-level adjustment
            </button>
          </div>
          <RefundComposer
            currency={so.currency ?? "AUD"}
            chargedTotal={so.total}
            alreadyRefunded={refundedAmount}
            tax={so.tax}
            orderShipping={so.shipping}
            shippingAlreadyRefunded={shippingAlreadyRefunded(data.refunds ?? [])}
            lines={refundLines}
            qtyByLine={refundQty}
            onQtyChange={(salesOrderItemId, qty) => setRefundQty((prev) => ({ ...prev, [salesOrderItemId]: qty }))}
            includeShipping={refundIncludeShipping}
            onIncludeShippingChange={setRefundIncludeShipping}
            adjustmentAmount={refundAdjustment}
            onAdjustmentAmountChange={setRefundAdjustment}
            adjustmentReason={refundAdjustmentReason}
            onAdjustmentReasonChange={setRefundAdjustmentReason}
            reason={refundReason}
            onReasonChange={setRefundReason}
            method={refundMethod}
            onMethodChange={setRefundMethod}
            provider={so.connectors?.provider ?? so.source}
            syncProvider={syncProviderRefund}
            onSyncProviderChange={setSyncProviderRefund}
            allowProviderSync={so.source === "bigcommerce" || so.source === "ebay"}
            unfulfilledRefundQty={unfulfilledRefundPreviewQty}
            disposition={refundDisposition}
            onDispositionChange={setRefundDisposition}
          />
        </div>
      </Modal>
    </div>
  );
}
